import { useEffect, useState } from 'react'

import { Box, FileText, Magnifier, Xmark } from '@gravity-ui/icons'
import { Button, Chip, EmptyState, InputGroup, Spinner, Table } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { Pagination } from '@/components/ui/Pagination'
import { useDebounce } from '@/hooks/useDebounce'
import { useUploadsStore } from '@/store/uploads.store'
import { formatDateTime } from '@/utils/dates'

export function UploadsTab() {
  const { t } = useTranslation()
  const {
    uploads,
    isLoading,
    filters,
    pagination,
    setFilters,
    setPagination,
    fetchUploads,
  } = useUploadsStore()

  const [searchValue, setSearchValue] = useState(filters.filename)
  const debouncedSearch = useDebounce(searchValue, 400)

  // Fetch data on initial mount
  useEffect(() => {
    fetchUploads()
  }, [fetchUploads])

  // Sync debounced search to store filters
  useEffect(() => {
    if (debouncedSearch !== filters.filename) {
      setFilters({ filename: debouncedSearch })
    }
  }, [debouncedSearch, filters.filename, setFilters])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <InputGroup className="w-80 max-w-xs" variant="secondary">
          <InputGroup.Prefix>
            <Magnifier className="size-4 text-zinc-400" />
          </InputGroup.Prefix>
          <InputGroup.Input
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            placeholder={t('uploads.search_placeholder', 'Поиск по имени файла...')}
          />
          {searchValue && (
            <InputGroup.Suffix>
              <Button
                isIconOnly
                aria-label="Clear"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSearchValue('')
                  setFilters({ filename: '' })
                }}
              >
                <Xmark className="size-3" />
              </Button>
            </InputGroup.Suffix>
          )}
        </InputGroup>
      </div>

      <div className="relative min-h-[300px]">
        {isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/50 backdrop-blur-xs">
            <Spinner size="lg" />
          </div>
        )}

        <Table className="min-h-[200px]">
          <Table.ScrollContainer className="max-h-[calc(100vh-theme(spacing.72))]">
            <Table.Content aria-label={t('uploads.table_aria_label', 'Логи загрузок файлов')}>
              <Table.Header className="sticky top-0 z-10">
                <Table.Column id="source" className="w-40">
                  {t('uploads.column_source', 'Область')}
                </Table.Column>
                <Table.Column id="filename">
                  {t('uploads.column_filename', 'Имя файла')}
                </Table.Column>
                <Table.Column id="uploaded_by">
                  {t('uploads.column_uploaded_by', 'Кто загрузил')}
                </Table.Column>
                <Table.Column id="uploaded_at" className="w-48">
                  {t('uploads.column_uploaded_at', 'Дата и время')}
                </Table.Column>
              </Table.Header>
              <Table.Body
                renderEmptyState={() => (
                  <EmptyState className="flex h-36 mt-6 w-full flex-col items-center justify-center gap-3 text-center">
                    <FileText className="size-8 text-zinc-400" />
                    <span className="text-sm text-zinc-500">
                      {t('common.no_results', 'Нет данных для отображения')}
                    </span>
                  </EmptyState>
                )}
              >
                {uploads.map(item => (
                  <Table.Row key={item.ID} className="hover:bg-default-50 transition-colors">
                    {/* Source Chip */}
                    <Table.Cell>
                      {item.Source === 'warehouse' ? (
                        <Chip
                          variant="soft"
                          color="success"
                          size="sm"
                          className="font-medium"
                        >
                          <Box className="size-3.5 inline shrink-0 mr-1" />
                          <span>{t('uploads.source_warehouse', 'Склад')}</span>
                        </Chip>
                      ) : (
                        <Chip
                          variant="soft"
                          color="accent"
                          size="sm"
                          className="font-medium"
                        >
                          <FileText className="size-3.5 inline shrink-0 mr-1" />
                          <span>{t('uploads.source_paid_roads', 'Платные дороги')}</span>
                        </Chip>
                      )}
                    </Table.Cell>

                    {/* Filename */}
                    <Table.Cell>
                      <span className="font-mono font-semibold text-foreground text-sm">
                        {item.Filename}
                      </span>
                    </Table.Cell>

                    {/* Uploaded By */}
                    <Table.Cell>
                      <div className="flex flex-col">
                        <span className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                          {item.UploadedByName || item.UploadedBy}
                        </span>
                        {item.UploadedByName && item.UploadedBy && (
                          <span className="text-xs text-zinc-400 font-mono">
                            {item.UploadedBy}
                          </span>
                        )}
                      </div>
                    </Table.Cell>

                    {/* Uploaded At */}
                    <Table.Cell>
                      <span className="font-mono text-xs text-zinc-600 dark:text-zinc-400">
                        {formatDateTime(item.UploadedAt)}
                      </span>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex justify-end pt-2">
          <Pagination
            page={pagination.page}
            total={pagination.totalPages}
            onChange={newPage => setPagination({ page: newPage })}
          />
        </div>
      )}
    </div>
  )
}
